/**
 * Conversión de UTC a la hora local del navegador.
 *
 * REGLA DURA del proyecto: el backend trabaja siempre en UTC (`timestamptz`) y nunca
 * compara fechas en horario local. La conversión a la hora del usuario es
 * responsabilidad EXCLUSIVA del frontend, y este módulo es el único sitio donde ocurre.
 *
 * Cómo se hace en la práctica: el backend serializa a ISO 8601 con sufijo `Z`
 * (`2026-02-07T15:00:00.000Z`). `new Date(iso)` lo interpreta como el instante absoluto
 * que es, y `Intl.DateTimeFormat` sin `timeZone` lo pinta en la zona del navegador. No
 * hay que sumar ni restar nada a mano: hacerlo es exactamente cómo se rompen estas cosas
 * dos veces al año, cuando cambia el horario de verano.
 *
 * ⚠️ Ninguna de estas funciones sirve para decidir si un pronóstico sigue abierto. Esa
 * decisión la toma el servidor con su propio reloj (regla dura de CLAUDE.md); aquí solo
 * se PINTA. El reloj del cliente puede estar mal, o mentir a propósito.
 */

/** Zona horaria que está usando el navegador. Útil para depurar y para enseñarla. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function toDate(utcIso: string | Date): Date | null {
  const date = utcIso instanceof Date ? utcIso : new Date(utcIso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Fecha y hora completas: "sam. 7 févr. 2026, 16:00". */
export function formatDateTime(utcIso: string | Date, locale: string): string {
  const date = toDate(utcIso);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** Solo la hora: "16:00". */
export function formatTime(utcIso: string | Date, locale: string): string {
  const date = toDate(utcIso);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date);
}

/** Solo la fecha: "7 février 2026". */
export function formatDate(utcIso: string | Date, locale: string): string {
  const date = toDate(utcIso);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/**
 * Valor para el atributo `dateTime` de <time>. Se deja en UTC ISO a propósito: es el
 * dato sin ambigüedad, y es lo que leen los lectores de pantalla y los buscadores.
 */
export function machineDateTime(utcIso: string | Date): string {
  return toDate(utcIso)?.toISOString() ?? '';
}
